import { Box, Button } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { displaySnackbar } from '../../../actions/main'
import { handleExternal, RouterRouteId } from '../../../routers/_props'
import CommonLoaderBackdrop from '../../Common/CommonLoaderBackdrop'
import DeletionBanner from '../../Common/DeletionBanner/DeletionBanner'
import SaveComponent from '../../Common/SaveComponent/SaveComponent'
import { usePatientFormContext } from '../context/PatientFormContext'
import PatientFormBodySection from './PatientFormBodySection'
import PatientFormHeader from './PatientFormHeader'
import PatientFormModificationsHistory from './PatientFormModificationsHistory/PatientFormModificationsHistory'
import PatientIdentity from './PatientIdentity/PatientIdentity'
import PatientIdentityStatus from './PatientIdentity/PatientIdentityStatus'
import useGetPatientInsiIdentityStatus from './PatientIdentity/_utils/useGetPatientInsiIdentityStatus'
import {
    FormatedPatientFormData,
    HandleIdentityCheckVoid,
    HandleSaveProps,
    PatientFormContextInsValues,
    PatientFormContextValues,
    PatientFormDisclaimerProps,
    PatientFormProps,
    SetIdentity,
    WorkbenchStatus,
} from './_props'
import useStyles from './_styles/PatientFormStyles'
import Address from './fields/Address'
import BirthCity from './fields/BirthCity'
import BirthDate from './fields/BirthDate'
import BirthName from './fields/BirthName'
import CountryBirth from './fields/CountryBirth'
import FirstFirstName from './fields/FirstFirstName'
import FirstName from './fields/FirstName'
import FirstNameList from './fields/FirstNameList'
import HasCss from './fields/HasCss'
import InsNum from './fields/InsNum'
import LastName from './fields/LastName'
import LtDiseases from './fields/LtDiseases'
import PatientIsSameAsInsured from './fields/PatientIsSameAsInsured'
import AttendingPhysician from './fields/AttendingPhysician'
import Room from './fields/Room'
import Sex from './fields/Sex'

import useTTDFlag from '../../../hooks/useTTDFlag'
import useUserPermissions from '../../../hooks/useUserPermissions'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import { usePatientFormModificationsHistory } from '../../../hooks/usePatientFormModificationsHistory'
import { useAppDispatch } from '../../../store'
import { ERROR_MESSAGE } from '../../../utils/ttdError'
import InformationalTooltip from '../../Common/InformationalTooltip'
import DowngradedIdentity from './PatientIdentity/DowngradedIdentity'
import Contact from './fields/Contact'
import InsOid from './fields/InsOid'
import PatientSocialSecurityNumber from './fields/PatientSocialSecurityNumber'
import PatientInsiResultDialog from './patientInsiResultDialog/PatientInsiResultDialog'
import usePatientServices from './services/usePatientServices'
import { usePatientIdentity, useRegradeIdentity } from './services/usePatientUtils'

import { InsiIdentityStatus2Ids } from '@toktokdoctech/tokapi-client'
import useAutomaticIdentityValidation from '../../../hooks/useAutomaticIdentityValidation'
import FormContainer from '../../Common/form/formContainer/FormContainer'
import { workbenchStatuses } from './_utils'
import AttachmentOrg from './fields/AttachmentOrg'
import { useLegacyHcs } from '../../legacy/LegacyHcsProvider'

const PatientForm = ({ data }: PatientFormProps) => {
    const classes = useStyles()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { id } = useParams<RouterRouteId>()
    const currentHcs = useCurrentHcs()
    const legacyHcs = useLegacyHcs()
    const isStripeActive = useTTDFlag('stripe')
    const arePatientsInmates = legacyHcs.patientsAreInmate === true || false
    const [shouldBlockNavigation, setShouldBlockNavigation] = React.useState(false)
    const { isAutomaticIdentityValidationDisabled } = useAutomaticIdentityValidation()
    const { hasUserPermission } = useUserPermissions()

    const [highLightedSectionTimeoutId, setHighLightedSectionTimeoutId] = React.useState<
        ReturnType<typeof setTimeout> | undefined
    >(undefined)
    const [highLightedSectionId, setHighLightedSectionId] = React.useState<string | undefined>(undefined)

    const { state, update } = usePatientFormContext()

    // Get the refetch function for patient modifications history
    const { refetch: refetchHistoryData } = usePatientFormModificationsHistory({
        hcsId: currentHcs?.id ?? '',
        patientId: id ?? '',
        isEditMode: state.mode === 'EDITION',
    })

    const form = useForm<FormatedPatientFormData>({
        mode: 'onChange',
        defaultValues: data,
    })

    const {
        getValues,
        formState: { errors },
        handleSubmit,
        reset,
        getFieldState,
        setError,
    } = form
    const formValues = getValues()

    const { handleUpdatePatient, handleCreatePatient, handleDeletePatient, isDeletingPatient, isSaving } =
        usePatientServices({
            formValues,
            data,
            reset,
            getFieldState,
            setError,
        })

    const handleIdentityChange = (
        result: InsiIdentityStatus2Ids | undefined,
        stateUpdate?: Partial<PatientFormContextValues>,
        stateInsUpdate?: Partial<PatientFormContextInsValues>,
    ) => {
        if ([1, 3].includes(result?.code ?? 1)) {
            form.setValue('insOid', '')
            form.setValue('insNum', '')
        }

        update({
            ...stateUpdate,
            ins: {
                ...state.ins,
                identityStatus: result,
                ...stateInsUpdate,
            },
        })
    }

    const { getStatusBySlug, loading: patientInsiIdentityStatusLoading } = useGetPatientInsiIdentityStatus()

    const { currentIdentityStatusSlug, currentIdentityStatus } = usePatientIdentity()

    const { regradeIdentity } = useRegradeIdentity()

    const isStatusLocked = React.useMemo(
        () => currentIdentityStatusSlug === 'RECOVERED' || currentIdentityStatusSlug === 'QUALIFIED',
        [currentIdentityStatusSlug],
    )

    const canUserEdit = React.useMemo(() => !isStatusLocked, [isStatusLocked])

    const isLoading = patientInsiIdentityStatusLoading || state.ins.isLoading

    const loaderMessage = React.useMemo(() => {
        if (state.loadingMessage) return state.loadingMessage
        if (isSaving) return 'Enregistrement des informations du patient...'
        if (isLoading) return 'Chargement des informations du patient...'
        return undefined
    }, [isLoading, isSaving, state.loadingMessage])

    let errorMessage = 'Veuillez corriger tous les champs comportant des erreurs pour pouvoir enregistrer.'
    
    const hasError = errors !== undefined && Object.keys(errors).length > 0
    const isDuplicatePatientError = hasError && !!errors.root?.message?.match(/Le patient est déjà existant/)

    if (isDuplicatePatientError && errors.root?.message) {
        errorMessage = errors.root.message
    }

    React.useEffect(() => {
        if (state.mode === 'EDITION') {
            form.trigger()
        }
    }, [currentIdentityStatusSlug, form, state.mode])

    React.useEffect(() => {
        update({
            isDisabled: !canUserEdit || !!data?.extUniqId,
        })
    }, [canUserEdit, data?.extUniqId, update])

    React.useEffect(() => {
        const res = form.formState.isDirty && Object.keys(form.formState.dirtyFields).length > 0

        setShouldBlockNavigation(res)
    }, [form.formState.isDirty, form.formState.dirtyFields])

    const handleWorkbenchStatus = React.useCallback(
        (hasInsConflicts?: boolean): WorkbenchStatus | undefined => {
            if (state.mode === 'CREATION') return undefined

            const hasIdentityStatusChange = currentIdentityStatus?.code !== state.originalIdentityStatus?.code
            const isConflicted = data?.lastInsiIdentityCheckResult === 'CONFLICT'
            const isProvisionalSlug = currentIdentityStatusSlug !== 'PROVISIONAL'

            if (isConflicted) {
                return hasIdentityStatusChange && isProvisionalSlug ? 'leave' : 'stay'
            }

            if (hasInsConflicts) {
                return 'enter'
            }

            return undefined
        },
        [
            currentIdentityStatus?.code,
            currentIdentityStatusSlug,
            data?.lastInsiIdentityCheckResult,
            state.mode,
            state.originalIdentityStatus?.code,
        ],
    )

    const handleSave = async (props?: HandleSaveProps) => {
        update({
            loading: true,
        })
        // eslint-disable-next-line no-async-promise-executor
        return new Promise<WorkbenchStatus | undefined>(async (resolve, reject) => {
            const workbenchStatus = handleWorkbenchStatus(props?.hasInsConflicts)

            setShouldBlockNavigation(false)
            const success = await form.trigger()
            if (!success) {
                dispatch(displaySnackbar('error', 'Veuillez corriger tous les champs'))
                reject()
            }

            try {
                if (state.mode === 'EDITION') {
                    await handleSubmit((e) => handleUpdatePatient(e, workbenchStatus))()
                } else {
                    await handleSubmit(handleCreatePatient)()
                }
                update({
                    loading: false,
                })
                resolve(workbenchStatus)
            } catch (err) {
                update({
                    loading: false,
                })
                if (err instanceof Error) {
                    if (err.message === ERROR_MESSAGE.ALREADY_EXIST) {
                        dispatch(
                            displaySnackbar(
                                'error',
                                'Le patient que vous souhaitez créer existe déjà dans votre base patient.',
                            ),
                        )
                    }
                } else {
                    dispatch(displaySnackbar('error', 'Une erreur est survenue'))
                }
                reject()
            }
        })
    }

    const handleCancel = () => {
        setShouldBlockNavigation(false)
        if (state.isExternal) {
            handleExternal('cancel')
        } else if (!state.isExternal) navigate('/patients')
    }

    const handleHightLight = (targetID: string) => {
        if (highLightedSectionTimeoutId) clearTimeout(highLightedSectionTimeoutId)
        setHighLightedSectionId(targetID)

        const target = document.getElementById(targetID)

        if (target) {
            window.scrollTo({
                top: target.offsetTop - 300,
            })
        }

        const timeoutId = setTimeout(() => {
            setHighLightedSectionId(undefined)
            clearTimeout(timeoutId)
        }, 3000)

        setHighLightedSectionTimeoutId(timeoutId)
    }

    const hasAddressWarning =
        !arePatientsInmates &&
        !(formValues.address && formValues.address.city && formValues.address.postalCode && formValues.address.street)
    const hasInsuranceWarning = formValues.hasLtDiseases == null || formValues.hasCss == null
    const hasContactWarning = !formValues.contactEmail || !formValues.contactPhone

    const getDisclaimerProps = (): PatientFormDisclaimerProps => {
        const textContents: React.ReactNode[] = []

        if (hasAddressWarning) {
            textContents.push(
                <>
                    Renseignez{' '}
                    <Button
                        title="&#x2693; Ancre vers la section: adresse"
                        color="inherit"
                        onClick={() => handleHightLight('adressAnchor')}
                        className={classes.PatientFormDisclamerButton}
                    >
                        ci-dessous
                    </Button>{' '}
                    les informations d'adresse manquantes.
                </>,
            )
        }

        if (hasInsuranceWarning) {
            textContents.push(
                <>
                    Renseignez{' '}
                    <Button
                        title="&#x2693; Ancre vers la section: informations de facturation"
                        color="inherit"
                        onClick={() => handleHightLight('facturationAnchor')}
                        className={classes.PatientFormDisclamerButton}
                    >
                        ci-dessous
                    </Button>{' '}
                    les informations de facturation manquantes. Ces informations sont disponibles sur l'attestation de
                    sécurité sociale du patient, ou sur{' '}
                    <a href="https://www.ameli.fr/" target="_blank" rel="noreferrer">
                        ameli.fr
                    </a>
                </>,
            )
        }

        return {
            displayed: state.mode === 'EDITION' && textContents.length > 0,
            textContents,
        }
    }

    const setIdentity: SetIdentity = (slug, stateUpdate, stateInsUpdate) =>
        getStatusBySlug(slug).then((status) => {
            if (isAutomaticIdentityValidationDisabled) {
                if (slug === 'VALIDATED' || slug === 'QUALIFIED') {
                    form.setValue('manualIdentityValidation', true)
                }
            }

            handleIdentityChange(status, stateUpdate, stateInsUpdate)
        })

    const handleIdentityDowngrade = async () => {
        form.setValue('insOid', '', {
            shouldDirty: true,
        })
        form.setValue('insNum', '', {
            shouldDirty: true,
        })
        form.setValue('manualIdentityValidation', false)

        const downgraded = regradeIdentity('downgrade', data?.identityProof?.value ?? 1)
        await setIdentity(downgraded, {
            isIdentityDowngraded: true,
        })
    }

    const handleIdentityCheck: HandleIdentityCheckVoid = async (hasInsConflicts) => {
        await handleSave({ hasInsConflicts })
        update({
            insiResultDialogOpen: hasInsConflicts,
        })
    }

    const handleManualIdentityValidation = async (checked: boolean) => {
        if (!checked && currentIdentityStatusSlug === 'QUALIFIED') {
            await setIdentity(regradeIdentity('set', undefined, 'RECOVERED'))
            return
        }

        const newIdentityStatus = checked
            ? regradeIdentity('upgrade', data?.identityProof?.value ?? 1)
            : regradeIdentity('reprocess', data?.identityProof?.value ?? 1)

        await setIdentity(newIdentityStatus)
    }

    return (
        <>
            <FormProvider {...form}>
                <FormContainer className={classes.PatientForm}>
                    <div
                        className={
                            state.isExternal ? classes.PatientFormContainerExternal : classes.PatientFormContainer
                        }
                    >
                        {formValues.fullName && (
                            <PatientFormHeader
                                title={formValues.fullName}
                                formDisclaimerProps={getDisclaimerProps()}
                                onDowngrade={handleIdentityDowngrade}
                            />
                        )}
                        <div className={classes.PatientFormBody}>
                            {!state.isExternal && (
                                <PatientFormBodySection
                                    label="Identité du patient"
                                    labelSideComponent={
                                        state.ins.identityStatus && (
                                            <PatientIdentityStatus
                                                status={state.ins.identityStatus}
                                                isLoading={patientInsiIdentityStatusLoading}
                                            />
                                        )
                                    }
                                >
                                    <PatientIdentity
                                        isLocked={!canUserEdit}
                                        setIdentity={setIdentity}
                                        onIdentityCheck={handleIdentityCheck}
                                        onManualIdentityValidation={handleManualIdentityValidation}
                                        onInsiSuccess={refetchHistoryData}
                                    />
                                </PatientFormBodySection>
                            )}
                            <PatientFormBodySection label="Fiche patient">
                                <BirthName />
                                <FirstNameList />
                                <FirstFirstName />
                                <FirstName />
                                <LastName />
                                <BirthDate />
                                <Sex />
                                <PatientSocialSecurityNumber />
                                <InsNum />
                                <InsOid />
                                <CountryBirth />
                                <BirthCity />
                                <Room />
                            </PatientFormBodySection>
                            <PatientFormBodySection
                                label="Informations de facturation"
                                id="facturationAnchor"
                                highlight={highLightedSectionId === 'facturationAnchor' ? 'WARNING' : undefined}
                                notification={hasInsuranceWarning ? 'WARNING' : undefined}
                            >
                                <PatientIsSameAsInsured />
                                <AttachmentOrg />
                                <LtDiseases />
                                <HasCss />
                            </PatientFormBodySection>
                            {!arePatientsInmates && (
                                <PatientFormBodySection
                                    notification={hasAddressWarning ? 'WARNING' : undefined}
                                    label="Adresse"
                                    id="adressAnchor"
                                    highlight={highLightedSectionId === 'adressAnchor' ? 'WARNING' : undefined}
                                >
                                    <Address />
                                </PatientFormBodySection>
                            )}
                            {isStripeActive && (
                                <PatientFormBodySection
                                    label="Coordonnées du patient ou de son représentant légal"
                                    notification={hasContactWarning ? 'WARNING' : undefined}
                                    labelSideComponent={
                                        <InformationalTooltip
                                            title="Les coordonnées de contacts seront utilisées dans le cadre des envois de documents (comme le compte-rendu, les ordonnances, notes d’honoraires,...) ou pour permettre l’utilisation du paiement en ligne si la situation s’y prête."
                                            placement="top"
                                        />
                                    }
                                >
                                    <Contact />
                                </PatientFormBodySection>
                            )}
                            <PatientFormBodySection label="Informations médicales">
                                <AttendingPhysician />
                            </PatientFormBodySection>
                        </div>
                        {state.mode === 'EDITION' && !state.isExternal && (
                            <PatientFormModificationsHistory margin="2rem 0" />
                        )}
                        <Box>
                            <PatientInsiResultDialog />
                            <DowngradedIdentity displayed={state.isIdentityDowngraded} />
                            <SaveComponent
                                saveButton={{
                                    action: () => {
                                        handleSave()
                                            .then((workbenchStatus) => {
                                                if (
                                                    !state.isExternal &&
                                                    workbenchStatuses.noConflict.includes(workbenchStatus)
                                                ) {
                                                    navigate('/patients')
                                                    
                                                }
                                            })
                                            .catch((err) => {
                                                console.error(err)
                                            })
                                    },
                                    // displayed: canUserEdit,
                                    displayed: hasUserPermission('USER'),
                                    isLoading: isSaving || isLoading,
                                }}
                                cancelButton={{ action: handleCancel }}
                                disableAll={isSaving || isLoading}
                                shouldBlockNavigation={shouldBlockNavigation}
                                errorMessage={hasError ? errorMessage : undefined}
                            />
                        </Box>
                    </div>
                    {state.mode === 'EDITION' && (
                        <DeletionBanner
                            bannerTitle="Êtes-vous sûr de vouloir supprimer le patient ?"
                            bannerLabel="Supprimer la fiche patient"
                            bannerSubLabel="Après confirmation, la suppression sera définitive"
                            onConfirm={handleDeletePatient}
                            isPending={isDeletingPatient}
                        />
                    )}
                </FormContainer>
            </FormProvider>
            {(loaderMessage || state.loading || isSaving) && <CommonLoaderBackdrop open label={loaderMessage} />}
        </>
    )
}

export default PatientForm
